import { z } from 'zod'
import type { ApiClient } from './client'
import { ackEnvelope, assertAck } from './envelope'

/**
 * Active Directory / LDAP sign-in — `/lc/mgmt/users/active_directory/*`.
 *
 * The path carries no organization, but every BODY does: AD is configured
 * per-organization, keyed by `orgName`. So this needs an organization chosen
 * before it can show anything.
 *
 * Two traps:
 *
 * - The status endpoint puts the verb IN THE PATH — `.../active_directory/enable`
 *   — and the vocabulary is `enable` / `disable`. That is a THIRD status
 *   vocabulary in this section: rows read `active`/`inactive`, user
 *   change_status writes `enabled`/`disabled`, and this writes `enable`/`disable`.
 *
 * - `adPassword` is base64-encoded by the CLIENT before it is sent, and comes
 *   back masked with asterisks. A value that is only asterisks means unchanged
 *   and must be omitted entirely — sending it stores the mask, exactly as the
 *   plugin config's SENSITIVE_MASK would.
 */
export const adConfig = z
  .object({
    adHost: z.string().default(''),
    adPort: z.string().default(''),
    authenticationType: z.string().default(''),
    adDomain: z.string().default(''),
    adBaseDn: z.string().default(''),
    ldapFilter: z.string().default(''),
    adEmailField: z.string().default(''),
    adUserNameField: z.string().default(''),
    adUserName: z.string().default(''),
    /** Masked on read. Never the real value. */
    adPassword: z.string().default(''),
    enabled: z.boolean().default(false),
  })
  .passthrough()

export type AdConfig = z.output<typeof adConfig>

export const adConfigResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    data: adConfig.nullable().default(null),
  })
  .transform((r) => r.data)

export const adSyncRunningResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    data: z.union([z.boolean(), z.object({}).passthrough()]).nullable().default(null),
  })
  .transform((r) => r.data === true)

/** What the status write takes, in its PATH. Not the other two vocabularies. */
export const AD_ENABLE = 'enable'
export const AD_DISABLE = 'disable'

/** The fields the config form sends. `enabled` is not one of them. */
export const AD_FIELDS = [
  'adHost', 'adPort', 'authenticationType', 'adDomain', 'adBaseDn',
  'ldapFilter', 'adEmailField', 'adUserNameField', 'adUserName',
] as const

/**
 * A password field that is only asterisks (or empty) is the mask, meaning
 * "unchanged". Same hazard as the plugin config's SENSITIVE_MASK: sending it
 * back stores the mask as the credential.
 */
export function isMaskedPassword(v: string): boolean {
  const t = v.trim()
  return t === '' || t.replace(/\*/g, '').trim() === ''
}

/**
 * base64, as the legacy client does with `btoa`.
 *
 * This is ENCODING, not encryption — the credential is recoverable by anyone
 * who sees the request. It is reproduced because the backend expects it, not
 * because it protects anything; the protection is that the request is over
 * TLS. Noted so nobody mistakes it for a security measure.
 */
function encodePassword(v: string): string {
  return btoa(v)
}

/**
 * The save body: `orgName`, the filled fields, and the password ONLY when it
 * is not the mask. Empty fields are dropped rather than sent as `''`, matching
 * the legacy form.
 */
export function buildAdSaveBody(orgName: string, values: Record<string, string>): Record<string, string> {
  const body: Record<string, string> = { orgName }
  for (const key of AD_FIELDS) {
    const v = values[key]
    if (v !== undefined && v !== null && v !== '') body[key] = v
  }
  const pw = values.adPassword ?? ''
  if (!isMaskedPassword(pw)) body.adPassword = encodePassword(pw)
  return body
}

export interface AdApi {
  config: (orgName: string) => Promise<AdConfig | null>
  save: (orgName: string, values: Record<string, string>) => Promise<void>
  setEnabled: (orgName: string, enabled: boolean) => Promise<void>
  sync: (orgName: string) => Promise<void>
  syncRunning: () => Promise<boolean>
}

export function createAdApi(client: ApiClient): AdApi {
  const ackOf = ackEnvelope
  return {
    config: async (orgName) =>
      client.request('GET', `/lc/mgmt/users/active_directory/config?orgName=${encodeURIComponent(orgName)}`, {
        schema: adConfigResponse,
      }),

    save: async (orgName, values) => {
      const res = await client.request('POST', '/lc/mgmt/users/active_directory/config', {
        body: buildAdSaveBody(orgName, values),
        schema: ackOf,
      })
      assertAck(res, 'Saving the directory configuration')
    },

    setEnabled: async (orgName, enabled) => {
      const status = enabled ? AD_ENABLE : AD_DISABLE
      // The verb is in the PATH as well as the body.
      const res = await client.request('POST', `/lc/mgmt/users/active_directory/${status}`, {
        body: { status, orgName },
        schema: ackOf,
      })
      assertAck(res, enabled ? 'Enabling directory sign-in' : 'Disabling directory sign-in')
    },

    sync: async (orgName) => {
      const res = await client.request('POST', '/lc/mgmt/users/active_directory/sync_data', {
        body: { orgName },
        schema: ackOf,
      })
      assertAck(res, 'Starting the sync')
    },

    syncRunning: async () =>
      client.request('GET', '/lc/mgmt/users/active_directory/sync_running', {
        schema: adSyncRunningResponse,
      }),
  }
}
